import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { useMemo, useState } from "react";

import { SortableItem } from "./sortable-item";
import { SortableContainer } from "./styled";
import type {
  SortableItemData as SortableItemType,
  SortableProps,
} from "./types";

export const Sortable = <T extends SortableItemType>({
  items,
  onReorder,
  renderItem,
  className,
  disabled = false,
  direction = "vertical",
  dragHandleSelector,
  animationDuration = 200,
  "aria-label": ariaLabel,
  onDragStart,
  onDragEnd,
}: SortableProps<T>): JSX.Element => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  const sortingStrategy = useMemo(
    () =>
      direction === "horizontal"
        ? horizontalListSortingStrategy
        : verticalListSortingStrategy,
    [direction],
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (disabled) return;

    const { active } = event;
    setActiveId(active.id as string);

    if (onDragStart) {
      const index = items.findIndex((item) => item.id === active.id);
      const item = items[index];
      if (item && index !== -1) {
        onDragStart(item, index);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (disabled || !over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }

    if (onDragEnd) {
      const item = items[oldIndex];
      if (item && oldIndex !== -1) {
        onDragEnd(item, oldIndex);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const ariaProps = {
    role: "list",
    "aria-label": ariaLabel || "Sortable list",
    "aria-live": "polite" as const,
    "aria-atomic": "false" as const,
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={itemIds} strategy={sortingStrategy}>
        <SortableContainer
          className={className}
          direction={direction}
          disabled={disabled}
          animationDuration={animationDuration}
          {...ariaProps}
        >
          {items.map((item, index) => {
            const isDragging = activeId === item.id;

            return (
              <SortableItem
                key={item.id}
                item={item}
                disabled={disabled}
                dragHandleSelector={dragHandleSelector}
              >
                {renderItem(item, index, isDragging)}
              </SortableItem>
            );
          })}
        </SortableContainer>
      </SortableContext>
    </DndContext>
  );
};
